/* Joi: <joi-gallery> — framed plate gallery with SEAMLESS infinite loop.
   Reimplements the intent of the Borders "JOI CUSTOMIZATIONS v6" clone loop,
   clean, on a transform track (no scroll-snap fighting):
   - clones of the last/first plate sit at both ends; advancing past an edge
     animates INTO the clone, then snaps invisibly to the real plate — no
     visible snap-back, both directions.
   - pointer drag with capture (>8px suppresses click-zoom, ±45px commits),
     horizontal wheel/trackpad (400 ms throttle, |ΔX|>|ΔY| and >25),
     arrows, thumbnails, dots. Edge resistance ×.35 when looping is off.
   - click toggles zoom scale(1.85); changing plate resets zoom.
   - RTL mirrors travel direction; prefers-reduced-motion drops animation.
   - videos: pause on slide change; FILM tile targets the video plate.
   - controls are real elements beside/over the frame edges only — nothing
     click-intercepting spans the plate. */

const config = JSON.parse(document.getElementById('joi-config')?.textContent || '{}');
const ARABIC_DIGITS = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* Joi: plate numerals are ALWAYS Arabic-Indic on the AR storefront (register
   rule) — unlike countdown digits, which are a separate opt-in setting. */
function plateDigits(str) {
  if (!config.isRtl) return str;
  return String(str).replace(/\d/g, (d) => ARABIC_DIGITS[+d]);
}

class JoiGallery extends HTMLElement {
  connectedCallback() {
    if (this.dataset.joiInit) return;
    this.dataset.joiInit = 'true';
    this.viewport = this.querySelector('[data-gallery-viewport]');
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;
    this.realSlides = Array.from(this.track.querySelectorAll('[data-gallery-slide]'));
    this.count = this.realSlides.length;
    this.loop = this.hasAttribute('data-loop') && this.count > 1;
    this.zoomEnabled = this.hasAttribute('data-zoom');
    this.rtl = document.documentElement.dir === 'rtl';
    this.logical = 0;
    this.phys = this.loop ? 1 : 0;
    this.drag = 0;
    this.dragging = false;
    this.suppressClick = false;
    this.zoomed = false;
    this.lastWheel = 0;

    if (this.loop) this.buildClones();
    this.bind();
    this.apply(false);
    this.syncUi();
  }

  buildClones() {
    const first = this.realSlides[0];
    const last = this.realSlides[this.count - 1];
    const mk = (src) => {
      const clone = src.cloneNode(true);
      clone.setAttribute('data-joi-clone', 'true');
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('id');
      clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      clone.querySelectorAll('a, button, video, iframe').forEach((el) => el.setAttribute('tabindex', '-1'));
      clone.querySelectorAll('img').forEach((img) => {
        img.loading = 'eager';
        img.removeAttribute('fetchpriority');
      });
      return clone;
    };
    this.track.insertBefore(mk(last), first);
    this.track.appendChild(mk(first));
  }

  get slides() { return Array.from(this.track.querySelectorAll('[data-gallery-slide]')); }

  bind() {
    const vp = this.viewport || this.track;

    vp.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.dragging = true;
      this.suppressClick = false;
      this.startX = e.clientX;
      try { vp.setPointerCapture(e.pointerId); } catch (err) { /* older engines */ }
    });
    vp.addEventListener('pointermove', (e) => {
      if (!this.dragging) return;
      let dx = e.clientX - this.startX;
      if (Math.abs(dx) > 8) this.suppressClick = true;
      if (!this.loop) {
        const atStart = this.logical === 0;
        const atEnd = this.logical === this.count - 1;
        const towardStart = this.rtl ? dx < 0 : dx > 0;
        const towardEnd = this.rtl ? dx > 0 : dx < 0;
        if ((atStart && towardStart) || (atEnd && towardEnd)) dx *= 0.35;
      }
      this.drag = dx;
      this.apply(false, true);
    });
    const release = (e) => {
      if (!this.dragging) return;
      this.dragging = false;
      const dx = e.clientX - this.startX;
      this.drag = 0;
      /* Joi: logical delta — in RTL a drag toward the left advances */
      const d = this.rtl ? -dx : dx;
      if (d < -45) this.next();
      else if (d > 45) this.prev();
      else this.apply(true);
    };
    vp.addEventListener('pointerup', release);
    vp.addEventListener('pointercancel', release);

    vp.addEventListener('wheel', (e) => {
      if (Math.abs(e.deltaX) < 25 || Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      const now = Date.now();
      if (now - this.lastWheel < 400) return;
      this.lastWheel = now;
      const forward = this.rtl ? e.deltaX < 0 : e.deltaX > 0;
      if (forward) this.next(); else this.prev();
    }, { passive: false });

    vp.addEventListener('dragstart', (e) => e.preventDefault());

    vp.addEventListener('click', (e) => {
      if (this.suppressClick) {
        e.preventDefault();
        e.stopPropagation();
        this.suppressClick = false;
        return;
      }
      if (!this.zoomEnabled) return;
      const slide = e.target.closest('[data-gallery-slide]');
      if (!slide || slide.dataset.mediaType === 'video' || slide.dataset.mediaType === 'external_video') return;
      this.zoomed = !this.zoomed;
      this.applyZoom();
    });

    this.querySelectorAll('[data-gallery-prev]').forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); this.prev(); }));
    this.querySelectorAll('[data-gallery-next]').forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); this.next(); }));
    this.querySelectorAll('[data-gallery-thumb]').forEach((t, i) => t.addEventListener('click', (e) => { e.preventDefault(); this.goTo(i); }));
    this.querySelectorAll('[data-gallery-dot]').forEach((d, i) => d.addEventListener('click', (e) => { e.preventDefault(); this.goTo(i); }));

    this.track.addEventListener('transitionend', (e) => {
      if (e.target !== this.track || !this.loop) return;
      if (this.phys === this.count + 1) { this.phys = 1; this.apply(false); }
      else if (this.phys === 0) { this.phys = this.count; this.apply(false); }
    });

    vp.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this.rtl ? this.prev() : this.next(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.rtl ? this.next() : this.prev(); }
    });
  }

  next() { this.step(1); }
  prev() { this.step(-1); }

  step(delta) {
    if (this.count < 2) return;
    this.resetZoom();
    if (this.loop) {
      /* Joi: fast repeated steps can outrun transitionend — normalise off the
         clone positions before stepping so the track never leaves the rail */
      if (this.phys >= this.count + 1) { this.phys = 1; this.apply(false); }
      else if (this.phys <= 0) { this.phys = this.count; this.apply(false); }
      this.phys += delta;
      this.logical = ((this.phys - 1) % this.count + this.count) % this.count;
      this.apply(true);
    } else {
      this.logical = Math.min(this.count - 1, Math.max(0, this.logical + delta));
      this.phys = this.logical;
      this.apply(true);
    }
    this.syncUi();
  }

  goTo(i) {
    if (i === this.logical || i < 0 || i >= this.count) return;
    this.resetZoom();
    this.logical = i;
    this.phys = this.loop ? i + 1 : i;
    this.apply(true);
    this.syncUi();
  }

  apply(animate, isDrag = false) {
    const factor = this.rtl ? 1 : -1;
    const animated = animate && !reduceMotion.matches && !isDrag;
    this.track.style.transition = animated ? 'transform .35s ease' : 'none';
    this.track.style.transform = `translateX(calc(${this.phys * 100 * factor}% + ${this.drag}px))`;
    if (!animated && !isDrag && this.loop) {
      /* force reflow so the invisible snap never animates */
      void this.track.offsetWidth;
    }
  }

  resetZoom() {
    if (!this.zoomed) return;
    this.zoomed = false;
    this.applyZoom();
  }

  applyZoom() {
    this.slides.forEach((s) => {
      const media = s.querySelector('img, video');
      if (!media) return;
      const active = !s.hasAttribute('data-joi-clone') && this.realSlides.indexOf(s) === this.logical;
      media.style.transition = reduceMotion.matches ? 'none' : 'transform .45s';
      media.style.transform = active && this.zoomed ? 'scale(1.85)' : 'none';
      s.style.cursor = this.zoomEnabled ? (active && this.zoomed ? 'zoom-out' : 'zoom-in') : '';
    });
  }

  syncUi() {
    const pad = (n) => plateDigits(String(n).padStart(2, '0'));
    this.querySelectorAll('[data-plate-current]').forEach((el) => { el.textContent = pad(this.logical + 1); });
    this.querySelectorAll('[data-gallery-thumb]').forEach((t, i) => {
      t.setAttribute('aria-current', i === this.logical ? 'true' : 'false');
    });
    this.querySelectorAll('[data-gallery-dot]').forEach((d, i) => {
      d.setAttribute('aria-current', i === this.logical ? 'true' : 'false');
    });
    this.realSlides.forEach((s, i) => {
      s.querySelectorAll('video').forEach((v) => { if (i !== this.logical) v.pause(); });
    });
    this.dispatchEvent(new CustomEvent('joi:gallery:change', { detail: { index: this.logical }, bubbles: true }));
  }
}

if (!customElements.get('joi-gallery')) customElements.define('joi-gallery', JoiGallery);
